import { REFRESH_TOKEN, TOKEN, USER } from '@synthetica-ai/constants';
import { httpRequest, ofType } from '@synthetica-ai/system';
import { localStorageValue, sessionStorageValue } from '@synthetica-ai/utils/storage';
import { combineEpics } from 'redux-observable';
import { EMPTY, from, of, catchError, first, ignoreElements, map, mergeMap, tap, } from 'rxjs';
import { openSnackbar } from 'models/ui';
import { PATHS } from 'routes/config';
import { redirectPromise, signInWithAzure } from 'services/azure';
import { parseStringOrArray } from 'utils/arrayUtils';
import { emptyAction } from 'utils/epics';
import { changePassword, checkCompany, checkToken, login, loginWithAzure, logout, register, resendVerificationCode, resetUserState, setRedirectPath, updateUser, validateAzureToken, verifyUser, } from './actions';
import * as services from './services';
const token = localStorageValue(TOKEN);
const refreshToken = localStorageValue(REFRESH_TOKEN);
const user = localStorageValue(USER);
const redirectAfterAuthEpic = (action$) => action$.pipe(first(), mergeMap(() => from(redirectPromise()).pipe(mergeMap((response) => response ? [validateAzureToken(response.accessToken)] : emptyAction()), catchError((err) => {
    console.log(err);
    return of(validateAzureToken.fail());
}))));
const validateAzureTokenEpic = (action$) => action$.pipe(ofType(validateAzureToken), httpRequest(action$, services.validateAzureToken));
const loginEpic = (action$) => action$.pipe(ofType(login), httpRequest(action$, services.loginService));
const loginWithAzureEpic = (action$) => action$.pipe(ofType(loginWithAzure), tap(() => signInWithAzure()), ignoreElements());
const loginSuccessEpic = (action$) => action$.pipe(ofType(login.success, validateAzureToken.success), map(({ payload }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    const cleanUserObject = {
        userid: (_a = payload === null || payload === void 0 ? void 0 : payload.user) === null || _a === void 0 ? void 0 : _a.userid,
        username: (_b = payload === null || payload === void 0 ? void 0 : payload.user) === null || _b === void 0 ? void 0 : _b.username,
        company: (_c = payload === null || payload === void 0 ? void 0 : payload.user) === null || _c === void 0 ? void 0 : _c.company,
        role: (_d = payload === null || payload === void 0 ? void 0 : payload.user) === null || _d === void 0 ? void 0 : _d.role,
        access: parseStringOrArray((_e = payload === null || payload === void 0 ? void 0 : payload.user) === null || _e === void 0 ? void 0 : _e.access),
        mail: (_f = payload === null || payload === void 0 ? void 0 : payload.user) === null || _f === void 0 ? void 0 : _f.mail,
        properties: ((_g = payload === null || payload === void 0 ? void 0 : payload.user) === null || _g === void 0 ? void 0 : _g.properties) || {},
        policy_accepted_on: (_h = payload === null || payload === void 0 ? void 0 : payload.user) === null || _h === void 0 ? void 0 : _h.policy_accepted_on,
        redirect_to: (_j = payload === null || payload === void 0 ? void 0 : payload.user) === null || _j === void 0 ? void 0 : _j.redirect_to,
        component_access: parseStringOrArray((_k = payload === null || payload === void 0 ? void 0 : payload.user) === null || _k === void 0 ? void 0 : _k.component_access),
        fleet: parseStringOrArray((_l = payload === null || payload === void 0 ? void 0 : payload.user) === null || _l === void 0 ? void 0 : _l.fleet),
        vessels: parseStringOrArray((_m = payload === null || payload === void 0 ? void 0 : payload.user) === null || _m === void 0 ? void 0 : _m.vessels),
        drydock_ids: parseStringOrArray((_o = payload === null || payload === void 0 ? void 0 : payload.user) === null || _o === void 0 ? void 0 : _o.drydock_ids),
        permissions: parseStringOrArray((_p = payload === null || payload === void 0 ? void 0 : payload.user) === null || _p === void 0 ? void 0 : _p.permissions),
    };
    const transformedPayload = Object.assign(Object.assign({}, payload), { user: cleanUserObject, _willRedirect: false });
    return transformedPayload;
}), tap((transformedPayload) => {
    const initialUrlSession = sessionStorageValue('initialUrl');
    const url = initialUrlSession.get();
    // Clear storage as this is a success path
    sessionStorage.clear(); // Clears initialUrl as well if it was in sessionStorage
    localStorage.clear();
    // Store the user data directly to localStorage
    localStorage.setItem(USER, JSON.stringify(transformedPayload.user));
    token.set(transformedPayload.auth.token);
    refreshToken.set(transformedPayload.auth.refreshToken);
    if (url) {
        transformedPayload._willRedirect = true;
        window.location.replace(url);
        return;
    }
    if (transformedPayload.user.redirect_to) {
        transformedPayload._willRedirect = true;
        window.location.replace(`${window.location.protocol}//${window.location.host}/${transformedPayload.user.redirect_to}`);
    }
}), mergeMap((transformedPayload) => {
    if (transformedPayload._willRedirect) {
        return EMPTY; // If redirection occurred, emit no further actions
    }
    // If no redirect, dispatch actions for the current page
    return [
        updateUser(transformedPayload.user),
        setRedirectPath(PATHS.PRODUCTS), // Fallback redirect
        openSnackbar({
            message: 'Successful login',
            type: 'success',
        }),
    ];
}));
const registerEpic = (action$) => action$.pipe(ofType(register), httpRequest(action$, services.registerService));
const registerSuccessEpic = (action$) => action$.pipe(ofType(register.success), tap((action) => {
    var _a;
    const email = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.email;
    if (email) {
        // Store the email in session storage for the verification page
        sessionStorage.setItem('verification_email', email);
    }
    else {
        console.error('No email found in registration response');
    }
}), mergeMap((action) => {
    var _a;
    return [
        ((_a = action.payload) === null || _a === void 0 ? void 0 : _a.email)
            ? setRedirectPath(PATHS.VERIFY)
            : emptyAction(),
    ];
}));
const verifyUserEpic = (action$) => action$.pipe(ofType(verifyUser), httpRequest(action$, services.verifyUserService));
const verifyUserSuccessEpic = (action$) => action$.pipe(ofType(verifyUser.success), mergeMap(() => [
    setRedirectPath(PATHS.CONGRATULATIONS),
    openSnackbar({
        message: 'Email verified successfully',
        type: 'success',
    }),
]));
const resendVerificationCodeEpic = (action$) => action$.pipe(ofType(resendVerificationCode), httpRequest(action$, services.resendVerificationCodeService));
const resendVerificationCodeSuccessEpic = (action$) => action$.pipe(ofType(resendVerificationCode.success), map(() => openSnackbar({
    message: 'Verification code has been resent to your email.',
    type: 'success',
})));
const logoutEpic = (action$) => action$.pipe(ofType(logout), httpRequest(action$, services.logoutService));
const logoutSuccessEpic = (action$) => action$.pipe(ofType(logout.success), tap(() => localStorage.clear()), mergeMap(() => [resetUserState()]));
const changePasswordEpic = (action$) => action$.pipe(ofType(changePassword), httpRequest(action$, services.changePasswordService));
const changePasswordSuccessEpic = (action$) => action$.pipe(ofType(changePassword.success), mergeMap(() => [
    setRedirectPath(PATHS.LOGIN),
    openSnackbar({
        message: 'Password changed successfully',
        type: 'success',
    }),
]));
const checkTokenEpic = (action$) => action$.pipe(ofType(checkToken), httpRequest(action$, services.checkTokenService));
const changeTokenSuccessEpic = (action$) => action$.pipe(ofType(checkToken.success), tap(({ payload }) => token.set(payload.token)), map(() => {
    // Get the user data from localStorage
    const userJson = localStorage.getItem(USER);
    const userData = userJson ? JSON.parse(userJson) : {};
    return updateUser(userData);
}));
const changeTokenFailEpic = (action$) => action$.pipe(ofType(checkToken.fail), tap(() => localStorage.clear()), ignoreElements());
const handleFailedRequestsEpic = (action$) => action$.pipe(ofType(login.fail, register.fail, logout.fail, changePassword.fail), map(({ payload }) => openSnackbar({
    message: payload.error || payload || 'Something went wrong',
    type: 'error',
})));
const handleVerificationFailuresEpic = (action$) => action$.pipe(ofType(verifyUser.fail, resendVerificationCode.fail), map(({ payload }) => openSnackbar({
    message: payload.error || payload || 'Verification failed. Please try again.',
    type: 'error',
})));
const checkCompanyEpic = (action$) => action$.pipe(ofType(checkCompany), httpRequest(action$, services.checkCompanyService));
export default combineEpics(loginEpic, registerEpic, registerSuccessEpic, logoutEpic, loginSuccessEpic, changePasswordEpic, changePasswordSuccessEpic, logoutSuccessEpic, checkTokenEpic, changeTokenSuccessEpic, changeTokenFailEpic, handleFailedRequestsEpic, loginWithAzureEpic, redirectAfterAuthEpic, validateAzureTokenEpic, checkCompanyEpic, verifyUserEpic, verifyUserSuccessEpic, resendVerificationCodeEpic, resendVerificationCodeSuccessEpic, handleVerificationFailuresEpic);
